import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import type { AspectRatio } from '@lumen5/luminary';

import { NarrativeThreadEnum, Urls } from 'js/src/client';
import AspectRatioPicker from 'js/src/components/app/AspectRatioPicker';
import { Toast } from 'js/src/components/common/Toast';
import { PrimaryButton, SPINNER_ICON } from 'js/src/components/common/ui/Button';
import { VideoPlanModalPreview } from 'js/src/components/video-planner/VideoPlanModalPreview';
import { isVideoLimitExceededInCycle } from 'js/src/data/account';
import { LOCKED_FEATURES, MODAL_IDS, showModal } from 'js/src/data/modals';
import type { VideoPlanWithCreationInfo } from 'js/src/data/video-planner';
import {
    generateVoiceoverForVideoPlan,
    saveVideoPlanVideoToDatabaseOperation,
} from 'js/src/data/video-planner';
import { ImmutableFromJS } from 'js/src/libs/consts';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import { useAppDispatch } from 'js/src/libs/RootState';
import { useLuminaryOperations } from 'js/src/luminary/luminary-hooks';

interface SpinoffsPreviewStageProps {
    selectedVideoPlan: VideoPlanWithCreationInfo;
    selectedAspectRatio: AspectRatio | null;
    isOpening: boolean;
    onAspectRatioChange: (aspectRatio: AspectRatio) => void;
    onBackToList: () => void;
    onHidden: () => void;
    setSavedPlanIds: React.Dispatch<React.SetStateAction<Set<string>>>;
    setIsOpening: React.Dispatch<React.SetStateAction<boolean>>;
}

export const SpinoffsPreviewStage = ({
    selectedVideoPlan,
    selectedAspectRatio,
    isOpening,
    onAspectRatioChange,
    onBackToList,
    onHidden,
    setSavedPlanIds,
    setIsOpening,
}: SpinoffsPreviewStageProps) => {
    const dispatch = useAppDispatch();
    const selectedPlanId = selectedVideoPlan.id;

    const { saveVideoPlanVideoToDatabase } = useLuminaryOperations({
        saveVideoPlanVideoToDatabaseOperation,
    });

    const isVideoLimitExceeded = useSelector(isVideoLimitExceededInCycle);

    const onSaveSpinoff = useCallback(
        async (e: React.MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();

            if (!selectedVideoPlan || !selectedAspectRatio) {
                return;
            }

            if (isVideoLimitExceeded) {
                onHidden();
                dispatch(
                    showModal({
                        modal_id: MODAL_IDS.FEATURE_LOCK,
                        modal_context: ImmutableFromJS({
                            featureName: LOCKED_FEATURES.VIDEO_LIMIT,
                        }),
                    }),
                );
                return;
            }

            setIsOpening(true);
            try {
                const video_id = await saveVideoPlanVideoToDatabase({
                    videoPlanId: selectedVideoPlan.id,
                    aspectRatio: selectedAspectRatio,
                    isSpinoff: true,
                });
                const url = Urls.APP + '?id=' + video_id;
                window.open(url, '_blank');
                setSavedPlanIds((prev) => new Set(prev).add(selectedVideoPlan.id));
                analytics.track('Spinoff Saved', {
                    videoId: video_id,
                });
                onBackToList();
            } catch (error) {
                ErrorReporter.capture(error);
                Toast.error('Failed to save spinoff. Please try again.');
            }
            setIsOpening(false);
        },
        [
            selectedVideoPlan,
            selectedAspectRatio,
            isVideoLimitExceeded,
            onHidden,
            dispatch,
            saveVideoPlanVideoToDatabase,
            onBackToList,
            setIsOpening,
            setSavedPlanIds,
        ],
    );

    if (!selectedVideoPlan) throw new Error('Invalid video plan');
    const hasPartiallyLoaded = selectedVideoPlan.scene_recommendations !== null;
    const hasFullyLoadedRecommendations =
        hasPartiallyLoaded &&
        selectedVideoPlan.scene_recommendations!.every((scene) => scene !== null);

    const isCreatingVoiceover = selectedVideoPlan?.voiceover.loading;
    const hasVoiceoverAsset = selectedVideoPlan?.voiceover.asset !== null;
    const isVoiceDriven =
        selectedVideoPlan?.narrative_thread === NarrativeThreadEnum.TALKING_HEAD ||
        selectedVideoPlan?.narrative_thread === NarrativeThreadEnum.VOICEOVER;

    // Generate voiceover if needed for preview stage
    useEffect(() => {
        if (!selectedPlanId || !hasFullyLoadedRecommendations) {
            return;
        }
        if (isVoiceDriven && !isCreatingVoiceover && !hasVoiceoverAsset) {
            void dispatch(generateVoiceoverForVideoPlan(selectedPlanId));
        }
    }, [
        dispatch,
        hasFullyLoadedRecommendations,
        hasVoiceoverAsset,
        isCreatingVoiceover,
        isVoiceDriven,
        selectedPlanId,
    ]);

    return (
        <div className="spinoffs-modal__preview">
            <h6 className="font-l5 font-l5--h6 mb-l5-16">{selectedVideoPlan.title}</h6>
            <VideoPlanModalPreview
                startMuted={false}
                autoplay={false}
                planId={selectedPlanId}
                hasFullyLoadedRecommendations={hasFullyLoadedRecommendations}
            />
            <div className="d-flex align-items-center justify-content-between mt-l5-16">
                <div className="d-flex align-items-center">
                    {selectedAspectRatio && (
                        <AspectRatioPicker
                            templateId={selectedVideoPlan.template_id}
                            selectedAspectRatio={selectedAspectRatio}
                            onAspectRatioChange={onAspectRatioChange}
                            containerClassName="me-l5-16"
                        />
                    )}
                </div>
                <PrimaryButton
                    onClick={onSaveSpinoff}
                    text="Save spinoff video"
                    disabled={!hasFullyLoadedRecommendations || isOpening}
                    iconLeft={isOpening ? SPINNER_ICON : null}
                />
            </div>
        </div>
    );
};
